import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Button, Input } from "@fluentui/react-components";
import { ArrowRight20Regular, Dismiss16Regular, Search20Regular } from "@fluentui/react-icons";
import { groupResults, tokenize } from "./Searchranking";
import { scheduleSearch, useSearchStore } from "./Searchstore";
import { SEARCH_APPS, appMeta } from "./Searchtypes";
import type { SearchAppId, SearchResult } from "./Searchtypes";
import { Highlight } from "./Searchui";
import styles from "./Searchresultspage.module.css";

interface Props {
  /** the app the user was in when they searched */
  currentApp: SearchAppId | null;
  token: string;
  onOpen: (r: SearchResult) => void;
}

const SHOW_FIRST = 6;

const AppBadge: React.FC<{ app: SearchAppId; size?: number }> = ({ app, size = 28 }) => {
  const m = appMeta(app);
  return (
    <span className={styles.srBadge} style={{ background: `${m.color}1a`, color: m.color, width: size, height: size }}>
      {m.label.charAt(0)}
    </span>
  );
};

const SearchResultsPage: React.FC<Props> = ({ currentApp, token, onOpen }) => {
  const query = useSearchStore((s) => s.query);
  const searched = useSearchStore((s) => s.searched);
  const results = useSearchStore((s) => s.results);
  const status = useSearchStore((s) => s.status);
  const running = useSearchStore((s) => s.running);
  const recent = useSearchStore((s) => s.recent);
  const setQuery = useSearchStore((s) => s.setQuery);
  const run = useSearchStore((s) => s.run);
  const remember = useSearchStore((s) => s.remember);
  const forgetRecent = useSearchStore((s) => s.forgetRecent);

  const [filter, setFilter] = useState<SearchAppId | "all">("all");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selKey, setSelKey] = useState<string | null>(null);

  const ctx = useMemo(() => ({ token, currentApp }), [token, currentApp]);
  const text = query.trim();
  const tokens = useMemo(() => tokenize(searched), [searched]);
  const here = currentApp ? appMeta(currentApp) : null;

  // Opening the page with a query that has not been searched yet (for example after Enter).
  useEffect(() => {
    if (text.length >= 2 && searched !== text) run(text, ctx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelKey(null);
  }, [searched]);

  const groups = useMemo(() => groupResults(results, currentApp), [results, currentApp]);
  const shownGroups = filter === "all" ? groups : groups.filter((g) => g.app === filter);
  const selected = results.find((r) => r.key === selKey) ?? null;

  const railApps = useMemo(() => {
    const order = SEARCH_APPS.map((a) => a.id);
    return [...order].sort((a, b) => {
      if (a === currentApp) return -1;
      if (b === currentApp) return 1;
      const ca = status[a]?.count ?? 0;
      const cb = status[b]?.count ?? 0;
      if (!!ca !== !!cb) return cb - ca;
      return order.indexOf(a) - order.indexOf(b);
    });
  }, [status, currentApp]);

  const errors = SEARCH_APPS.filter((a) => status[a.id]?.state === "error");
  const notConnected = SEARCH_APPS.filter((a) => status[a.id]?.state === "unavailable");

  const onType = (v: string) => {
    setQuery(v);
    scheduleSearch(v, ctx);
  };
  const open = (r: SearchResult) => {
    remember(searched || text);
    onOpen(r);
  };

  return (
    <div className={styles.srRoot}>
      <div className={styles.srTop}>
        <div className={styles.srTopInner}>
          <Input
            className={styles.srInput}
            size="large"
            autoFocus
            contentBefore={<Search20Regular />}
            contentAfter={
              text ? (
                <Button
                  appearance="transparent"
                  size="small"
                  aria-label="Clear search"
                  icon={<Dismiss16Regular />}
                  onClick={() => {
                    setQuery("");
                    useSearchStore.getState().clear();
                  }}
                />
              ) : undefined
            }
            placeholder="Search every app"
            value={query}
            onChange={(_, d) => onType(d.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && text.length >= 2) run(text, ctx);
            }}
          />
          {here && (
            <span className={styles.srHint}>
              Searching <b style={{ color: here.color }}>{here.label}</b> first, then everything else.
            </span>
          )}
        </div>
      </div>

      {text.length < 2 && searched === "" ? (
        <div className={styles.srBody1}>
          <div className={styles.srStart}>
            <span className={styles.srStartIcon}>
              <Search20Regular />
            </span>
            <h3 className={styles.srStartTitle}>Search across all your apps</h3>
            <p className={styles.srMuted}>
              Rooms, customers, vendors, leads, products, items, workers, settings and help. Type at least two letters.
            </p>
            {recent.length > 0 && (
              <div className={styles.srRecent}>
                <div className={styles.srRecentHead}>
                  <span className={styles.srLabel}>Recent searches</span>
                  <button type="button" className={styles.srLink} onClick={forgetRecent}>
                    Clear
                  </button>
                </div>
                <div className={styles.srChips}>
                  {recent.map((r) => (
                    <button key={r} type="button" className={styles.srChip} onClick={() => onType(r)}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.srBody}>
          <nav className={styles.srRail} aria-label="Filter by app">
            <button
              type="button"
              className={`${styles.srRailItem} ${filter === "all" ? styles.srRailOn : ""}`}
              onClick={() => setFilter("all")}
            >
              <span className={styles.srRailName}>All apps</span>
              <span className={styles.srCount}>{results.length}</span>
            </button>
            {railApps.map((id) => {
              const m = appMeta(id);
              const st = status[id];
              const off = st?.state === "unavailable";
              return (
                <button
                  key={id}
                  type="button"
                  disabled={off}
                  className={`${styles.srRailItem} ${filter === id ? styles.srRailOn : ""}`}
                  onClick={() => setFilter(id)}
                  title={off ? "Search for this app is not connected yet" : undefined}
                >
                  <span className={styles.srDot} style={{ background: off ? "#d1d5db" : m.color }} />
                  <span className={styles.srRailName}>
                    {m.label}
                    {id === currentApp && <span className={styles.srHere}>You&apos;re here</span>}
                  </span>
                  {st?.state === "loading" ? (
                    <span className={styles.srSpin} aria-label="Searching" />
                  ) : st?.state === "error" ? (
                    <span className={styles.srErr}>!</span>
                  ) : off ? (
                    <span className={styles.srOff}>soon</span>
                  ) : (
                    <span className={styles.srCount}>{st?.count ?? 0}</span>
                  )}
                </button>
              );
            })}
          </nav>

          <section className={styles.srResults} aria-live="polite">
            <div className={styles.srSummary}>
              {running && results.length === 0
                ? `Searching for “${searched}”…`
                : `${results.length} result${results.length === 1 ? "" : "s"} for “${searched}”${running ? " · still searching" : ""}`}
            </div>

            {errors.length > 0 && (
              <div className={styles.srNotice}>
                {errors.map((a) => (
                  <div key={a.id} className={styles.srNoticeRow}>
                    <span>
                      <b>{a.label}</b> couldn&apos;t be searched: {status[a.id].error}
                    </span>
                    <button type="button" className={styles.srLink} onClick={() => run(text, ctx)}>
                      Try again
                    </button>
                  </div>
                ))}
              </div>
            )}

            {running && results.length === 0 && (
              <div className={styles.srCard}>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={styles.srSkel}>
                    <span className={styles.srSkelBox} />
                    <span className={styles.srSkelLines}>
                      <span />
                      <span />
                    </span>
                  </div>
                ))}
              </div>
            )}

            {!running && results.length === 0 && (
              <div className={styles.srCard}>
                <div className={styles.srEmpty}>
                  <strong>Nothing found for “{searched}”</strong>
                  <span>Check the spelling, try fewer words, or search by a code or number.</span>
                </div>
              </div>
            )}

            {shownGroups.map((g) => {
              const m = appMeta(g.app);
              const limit = filter === "all" && !expanded[g.app] ? SHOW_FIRST : g.items.length;
              return (
                <div key={g.app} className={styles.srCard}>
                  <div className={styles.srGroupHead}>
                    <AppBadge app={g.app} size={26} />
                    <span className={styles.srGroupName}>{m.label}</span>
                    {g.app === currentApp && <span className={styles.srHere}>You&apos;re here</span>}
                    <span className={styles.srCount}>{g.items.length}</span>
                  </div>
                  <ul className={styles.srList}>
                    {g.items.slice(0, limit).map((r) => (
                      <li key={r.key}>
                        <div
                          role="button"
                          tabIndex={0}
                          className={`${styles.srRow} ${selKey === r.key ? styles.srRowOn : ""}`}
                          onClick={() => setSelKey(r.key)}
                          onDoubleClick={() => open(r)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") open(r);
                            if (e.key === " ") {
                              e.preventDefault();
                              setSelKey(r.key);
                            }
                          }}
                        >
                          <span className={styles.srKind}>{r.kind}</span>
                          <span className={styles.srRowText}>
                            <span className={styles.srRowTitle}>
                              <Highlight text={r.title} tokens={tokens} />
                            </span>
                            {r.subtitle && (
                              <span className={styles.srRowSub}>
                                <Highlight text={r.subtitle} tokens={tokens} />
                              </span>
                            )}
                          </span>
                          <Button
                            className={styles.srOpen}
                            size="small"
                            appearance="subtle"
                            icon={<ArrowRight20Regular />}
                            aria-label={`Open ${r.title}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              open(r);
                            }}
                          >
                            Open
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {filter === "all" && g.items.length > SHOW_FIRST && (
                    <button
                      type="button"
                      className={styles.srMore}
                      onClick={() => setExpanded((e) => ({ ...e, [g.app]: !e[g.app] }))}
                    >
                      {expanded[g.app] ? "Show fewer" : `Show all ${g.items.length}`}
                    </button>
                  )}
                </div>
              );
            })}

            {notConnected.length > 0 && results.length + errors.length > 0 && (
              <p className={styles.srFoot}>
                Not searched yet: {notConnected.map((a) => a.label).join(", ")}.
              </p>
            )}
          </section>

          <aside className={styles.srDetail} aria-label="Result details">
            {selected ? (
              <>
                <div className={styles.srDetailHead}>
                  <AppBadge app={selected.app} size={36} />
                  <div className={styles.srDetailTitles}>
                    <h4 className={styles.srDetailTitle}>{selected.title}</h4>
                    <span className={styles.srMuted}>
                      {appMeta(selected.app).label} · {selected.kind}
                    </span>
                  </div>
                </div>
                <dl className={styles.srFacts}>
                  {selected.fields.map(([k, v]) => (
                    <div key={k} className={styles.srFact}>
                      <dt>{k}</dt>
                      <dd>{v}</dd>
                    </div>
                  ))}
                </dl>
                <Button appearance="primary" icon={<ArrowRight20Regular />} onClick={() => open(selected)}>
                  Open in {appMeta(selected.app).label}
                </Button>
              </>
            ) : (
              <div className={styles.srDetailEmpty}>Select a result to see its details here.</div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default SearchResultsPage;